/**
 * Folha inferior (bottom sheet) que se arrasta para baixo para fechar.
 *
 * O `.sheet` puro fechava só pelo toque fora do painel; no celular a pessoa
 * puxa a alça para baixo, como em qualquer app (teste físico, item 5). O
 * painel segue o dedo (`translateY`), e soltar depois de 1/4 da altura — ou
 * com velocidade — fecha; antes disso, volta ao lugar. O arrasto só começa
 * quando o conteúdo está no topo, para não brigar com a rolagem interna.
 */
import { useEffect, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';

const FECHA_A_PARTIR_DE = 0.25;
const VELOCIDADE_MIN_PX_MS = 0.6;
/** Um flick só conta como flick depois de andar isto; menos é tremor do dedo. */
const FLICK_MIN_PX = 48;

export function useArrastarParaFechar(onFechar: () => void) {
  const painel = useRef<HTMLDivElement>(null);
  const inicio = useRef<{ y: number; t: number } | null>(null);
  // O deslocamento vive num ref E num estado: o ref decide (um flick rápido
  // dispara move e end antes de o React renderizar), o estado desenha.
  const deslocRef = useRef(0);
  const [desloc, setDesloc] = useState(0);
  const [soltando, setSoltando] = useState(false);
  const mover = (px: number) => {
    deslocRef.current = px;
    setDesloc(px);
  };

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const el = painel.current;
    if (!el || e.touches.length !== 1) return;
    // Conteúdo rolado: o gesto é rolagem, não fechamento.
    if (el.scrollTop > 0) return;
    inicio.current = { y: e.touches[0]!.clientY, t: Date.now() };
    setSoltando(false);
  };
  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!inicio.current) return;
    const dy = e.touches[0]!.clientY - inicio.current.y;
    mover(Math.max(0, dy));
  };
  const onTouchEnd = () => {
    const el = painel.current;
    if (!inicio.current || !el) return;
    const dt = Math.max(1, Date.now() - inicio.current.t);
    const percorrido = deslocRef.current;
    const velocidade = percorrido / dt;
    inicio.current = null;
    if (percorrido > el.offsetHeight * FECHA_A_PARTIR_DE || (percorrido >= FLICK_MIN_PX && velocidade > VELOCIDADE_MIN_PX_MS)) {
      onFechar();
      return;
    }
    setSoltando(true);
    mover(0);
  };

  return {
    ref: painel,
    style: desloc ? { transform: `translateY(${desloc}px)`, transition: 'none' } : soltando ? { transition: 'transform 180ms ease-out' } : undefined,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
  };
}

export function Sheet({
  titleId,
  onFechar,
  children,
  className = '',
  testId,
}: {
  titleId: string;
  onFechar: () => void;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  const arrasto = useArrastarParaFechar(onFechar);
  // Fecha também com Esc (teclado físico, leitor de tela): scrim, alça ou Esc.
  // Com duas folhas empilhadas, só a de cima responde.
  const raiz = useRef<HTMLDivElement>(null);
  const fechar = useRef(onFechar);
  fechar.current = onFechar;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const folhas = document.querySelectorAll('.sheet');
      if (folhas[folhas.length - 1] === raiz.current) fechar.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // A folha vai direto para o <body>, fora do `.app-scroll` (Onda 1, 15/09,
  // #83): no iOS, `position: fixed` dentro da área que rola (animação de
  // entrada + overflow-scrolling) passava a valer em relação a ela — a folha
  // terminava na barra de abas, o painel era cortado e nada rolava. Os
  // eventos continuam subindo pela árvore React (quem abriu continua
  // controlando o fechar); só o DOM muda de lugar.
  return createPortal(
    <div className="sheet" ref={raiz} role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={onFechar}>
      <div className={`sheet__panel ${className}`.trim()} onClick={(e) => e.stopPropagation()} data-testid={testId} {...arrasto}>
        <div className="sheet__handle" aria-hidden="true" />
        {children}
      </div>
    </div>,
    document.body,
  );
}
